import { useContext } from "react";

import { Select, type SelectValue } from "ui/select";
import { Typography } from "ui/typography";
import { CommentInput } from "containers/admin/clients/touchpoint/components/comments/comment-input/comment-input";
import {
  FABDivider,
  FABHeader,
} from "components/partials/general-comments-fab/general-comments-fab";

import CommentContext from "contexts/comment-context";

import { useCurrentUser } from "hooks/api/use-current-user";

import { hasPermission } from "utilities/user";

import { Permission } from "types/auth";
import { CommentFilterOption, commentFilterOptions } from "types/comments";

interface CommentsHeaderProps {
  isClientUser: boolean;
}

export const CommentsHeader = ({ isClientUser }: CommentsHeaderProps) => {
  const currentUser = useCurrentUser();
  const canComment = hasPermission(currentUser, Permission.PERM_COMMENT_WRITE);

  const { commentsSummary, commentFilter, setCommentFilter, submitNewComment, commentingDisabled } =
    useContext(CommentContext);

  const filterOptions = commentFilterOptions.filter((option: CommentFilterOption) =>
    isClientUser ? option.id !== "visible-to-client-only" : true,
  );

  const handleSelect = (selected: SelectValue<CommentFilterOption>) => {
    const chosen = filterOptions.find((option) => option === selected);

    if (chosen) setCommentFilter(chosen);
  };

  return (
    <>
      <FABHeader actionItemCount={commentsSummary.actionItemCount}>
        <Select<CommentFilterOption>
          selectName="comment-filter"
          ariaLabel="Filter comments"
          options={filterOptions}
          value={commentFilter}
          onChange={handleSelect}
          disableClearable
        />
        <Typography variant="h4" sx={{ color: "grey.900", fontSize: "0.875rem" }}>
          Viewing {commentsSummary.openCount} open comment
          {commentsSummary.openCount === 1 ? "" : "s"}
        </Typography>
      </FABHeader>

      {canComment && (
        <>
          <FABDivider />
          <CommentInput
            submitNewComment={submitNewComment}
            isClientUser={isClientUser}
            isDisabled={commentingDisabled}
          />
        </>
      )}
    </>
  );
};
